import { AppContext } from "../core/context";
import { productLibrary } from "../data";
import { Configuration, Connector, ConnectorInfo, Entity, EntityCreateCallback, Module, ModuleCreateCallback, Product } from "../types/type_def";
import { ConversionHelper } from "../utils/conversion";
import { MathHelper } from "../utils/math";
import { createCenter, createEntityActions } from "./actions";
import { createEntityConnectors, getConnectedConnectorInfo, getMatchingConnectors, removeEntityConnections } from "./connectors";

export function getCurrentEntity(ctx: AppContext) {
    if (ctx.currentEntityNode) {
        return ctx.currentModule.entities.find(function (e) { return e.uniqueId === ctx.currentEntityNode.uniqueId });
    }
    return null;
}

export function getParentMesh(ctx: AppContext, mesh: BABYLON.Node, tags?: string): BABYLON.Node {

    if (BABYLON.Tags.HasTags(mesh.parent)) {
        if (BABYLON.Tags.MatchesQuery(mesh.parent, tags)) {
            return mesh.parent;
        }
    }

    return getParentMesh(ctx, mesh.parent, tags);
}

export function instantiateModule(ctx: AppContext, configuration: Configuration, module?: Module, callback?: ModuleCreateCallback) {

    let moduleNode: BABYLON.TransformNode = null;

    if (!module) {

        module = {} as Module;
        module.id = "module" + Date.now();
        module.position = BABYLON.Vector3.Zero();
        module.rotation = BABYLON.Vector3.Zero();
        module.entities = [];

        configuration.data.modules.push(module);
    }

    moduleNode = ctx.scene.getTransformNodeByUniqueId(module.uniqueId);
    if (!moduleNode) {
        moduleNode = new BABYLON.TransformNode(module.id, ctx.scene);
        module.uniqueId = moduleNode.uniqueId;
        BABYLON.Tags.AddTagsTo(moduleNode, "module");
    }

    moduleNode.position = new BABYLON.Vector3(module.position._x, module.position._y, module.position._z);
    moduleNode.rotation = new BABYLON.Vector3(module.rotation._x, module.rotation._y, module.rotation._z);
    moduleNode.metadata = {};
    moduleNode.metadata.meshtype = "module";
    moduleNode.parent = ctx.modulesNode;
    moduleNode.computeWorldMatrix(true);

    if (callback) {
        callback(module, moduleNode);
    }

    return { module, moduleNode }
}

export function instantiateEntity(ctx: AppContext, module: Module, product: Product, entity?: Entity, callback?: EntityCreateCallback) {   
        
    if (!product)
        return;

    var moduleNode = ctx.scene.getTransformNodeByUniqueId(module.uniqueId);

    if (!entity) {

        entity = {} as Entity;
        entity.id = "entity" + Date.now();
        entity.connections = [];
        entity.connectors = [];
        entity.position = BABYLON.Vector3.Zero();
        entity.rotation = BABYLON.Vector3.Zero();
        entity.productNo = product.productNo;
        entity.parentId = moduleNode.id;
        entity.category = product.type;

        module.entities.push(entity);
    }

    //Entity node
    let entityNode = new BABYLON.TransformNode(entity.id, ctx.scene);
    entity.uniqueId = entityNode.uniqueId;
    entityNode.metadata = {};
    entityNode.metadata.meshtype = "entity";
    entityNode.metadata.productNo = entity.productNo;
    entityNode.rotation = BABYLON.Vector3.Zero();

    entityNode.computeWorldMatrix(true);

    var entityAttachNode = null;
    if (entity.attachId) {
        var parentNode = ctx.scene.getNodeById(entity.parentId);
        entityAttachNode = parentNode.getChildren(function (c) { return c.id === entity.attachId }, false)[0];
    } else {
        entityAttachNode = moduleNode;
    }

    entityNode.parent = entityAttachNode;

    BABYLON.Tags.EnableFor(entityNode);
    BABYLON.Tags.AddTagsTo(entityNode, "entity");

    //Entitybounds
    var entityBounds = BABYLON.MeshBuilder.CreateBox("entitybounds", {
        width: ConversionHelper.scale(product.width, ctx.scaleFactor) as number,
        height: ConversionHelper.scale(product.height, ctx.scaleFactor) as number,
        depth: ConversionHelper.scale(product.depth, ctx.scaleFactor) as number
    }, ctx.scene);
    var entityExtendSize = entityBounds.getBoundingInfo().boundingBox.extendSize;
    entityBounds.position.addInPlace(new BABYLON.Vector3(0, 0, entityExtendSize.z));

    entityBounds.parent = entityNode;        
    entityBounds.visibility = 0.5;
    entityBounds.isPickable = true;
    entityBounds.setEnabled(false);

    entityBounds.setPivotMatrix(BABYLON.Matrix.Translation(0, entityBounds.getBoundingInfo().boundingBox.extendSize.y, 0), false);
    entityNode.setPivotPoint(entityBounds.position, BABYLON.Space.LOCAL);
    entityNode.computeWorldMatrix(true);

    BABYLON.Tags.EnableFor(entityBounds);
    BABYLON.Tags.AddTagsTo(entityBounds, "entitybounds");

    if (productLibrary.getProductCategory(entity.category) === "UPHOLSTRY") {

        //Create center
        createCenter(ctx, module, entity, ctx.scene);

        //Create actions for entity
        createEntityActions(ctx, module, entity, ctx.scene, product);

        //Create connectors for entity
        createEntityConnectors(ctx, module, entity, product, ctx.scene);   
    }

    entityNode.position = ConversionHelper.toVector3(entity.position);
    entityNode.rotation = ConversionHelper.toVector3(entity.rotation);

    if (callback) {
        callback(entity, entityNode, entity.productNo);
    }
  
    return { entity, entityNode }
}

export function createEntityWeights(ctx: AppContext, entity: Entity) {

    var entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
    if (entityNode) {

       // let offset = ConversionHelper.scale(30, scaleFactor) as number;

        //var weightPosition = bb.center.subtract(new BABYLON.Vector3(0, 0, offset));

        //TODO - Needs to be part of model
        /*if (bb.extendSize.x / bb.extendSize.z === 1) {
            weightPosition = bb.center.subtract(new BABYLON.Vector3(offset, 0, offset));
        }*/

       // var weightPosition = bb.center

        var cameraPos = ctx.topviewCamera.position;
        cameraPos.y = entityNode.position.y;
        cameraPos.z = 10;
        cameraPos.normalize();

        var entitytPos = ctx.rootNode.position.clone();
        entitytPos.normalize();

        var angle =  BABYLON.Vector3.GetAngleBetweenVectors(cameraPos, entitytPos, BABYLON.Vector3.Up());      

        var weight = BABYLON.MeshBuilder.CreateBox("weight", { size: 10 * ctx.scaleFactor }, ctx.scene);
        weight.setEnabled(false);           
        weight.rotation = new BABYLON.Vector3(0, Math.PI/4 + angle, 0)
        weight.parent = entityNode;           
       
    }
}

export function weighConnection(ctx: AppContext, module: Module, entity: Entity, connectorName: string, traversedIds: Array<number>, weight: number) {

    if (traversedIds.indexOf(entity.id) === -1) {

        traversedIds.push(entity.id);
        weight += 1;

        entity.connections.forEach(function (c) {

            if (c.id !== connectorName) {
                var nextEntity = ctx.moduleHelper.getEntityByID(module, c.entityId);

                if (nextEntity) {
                    nextEntity.connections.forEach(function (c) {
                        weight = weighConnection(ctx, module, nextEntity, connectorName, traversedIds, weight);
                    });
                }
            }
        });
    }

    return weight;
}

export function setTargetRotation(ctx: AppContext, entity: Entity, parentNode: BABYLON.TransformNode, parentConnector: BABYLON.TransformNode, childNode: BABYLON.TransformNode, childConnector: BABYLON.TransformNode) {

    var parentRotation = BABYLON.Quaternion.Identity();
    parentConnector.getWorldMatrix().decompose(BABYLON.Vector3.Zero(), parentRotation, BABYLON.Vector3.Zero());

    var childRotation = BABYLON.Quaternion.Identity();
    childConnector.getWorldMatrix().decompose(BABYLON.Vector3.Zero(), childRotation, BABYLON.Vector3.Zero());

    let newRotationAngle = MathHelper.normalizePi((parentRotation.toEulerAngles().y - childRotation.toEulerAngles().y));
    let newRotation = new BABYLON.Vector3(0, newRotationAngle, 0);

    ctx.targetRotation = childNode.rotation.add(newRotation);   
    entity.rotation = ctx.targetRotation     
    childNode.computeWorldMatrix(true);
}

/* let animateRotation = function (node, targetRotation) {
animRotation = BABYLON.Animation.CreateAndStartAnimation("animRotate", node, "rotation", 60, 30, node.rotation, targetRotation, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT, easingFunction);
}*/

export function moveEntity(ctx: AppContext, entity: Entity, parentNode: BABYLON.TransformNode, parentConnector: BABYLON.TransformNode, childNode: BABYLON.TransformNode, childConnector: BABYLON.TransformNode) {

    if (!childConnector || !parentConnector)
        return;

    parentNode.computeWorldMatrix(true);
    parentConnector.computeWorldMatrix(true);
    childConnector.computeWorldMatrix(true);

    const localForward = new BABYLON.Vector3(0, 0, 1);

    var parentNormal = BABYLON.Vector3.TransformNormal(localForward, parentConnector.getWorldMatrix());
    parentNormal.y = 0;
    if (parentNormal.length() > 0.001) { parentNormal.normalize(); }

    var childNormal = BABYLON.Vector3.TransformNormal(localForward, childConnector.getWorldMatrix());
    childNormal.y = 0;
    if (childNormal.length() > 0.001) { childNormal.normalize(); }

    var targetNormal = parentNormal.negate();
    const currentAngle = Math.atan2(childNormal.x, childNormal.z);
    const targetAngle = Math.atan2(targetNormal.x, targetNormal.z);
    const rawAngle = targetAngle - currentAngle;
    const step = Math.PI / 180;
    let newRotationAngle = MathHelper.normalizePi(Math.round(rawAngle / step) * step);
    let newRotation = new BABYLON.Vector3(0, newRotationAngle, 0);

    childNode.rotation.addInPlace(newRotation);
    entity.rotation = childNode.rotation;
    childConnector.computeWorldMatrix(true);

    var positionDiff = parentConnector.getAbsolutePosition().subtract(childConnector.getAbsolutePosition());     
           
    childNode.position = childNode.position.add(positionDiff);
    entity.position = childNode.position
    childNode.computeWorldMatrix(true);
    
}

export function moveConnectedEntities(ctx: AppContext, entity: Entity, traversedIds: Array<string>) {

    if (!traversedIds.includes(entity.id)) {

        traversedIds.push(entity.id);

        entity.connections.forEach(function (connection) {

            let entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
            if (entityNode) {
                let entityConnectorNode = entityNode.getChildMeshes(true, function (c) { return c.name === connection?.id })[0];

                var connectedEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, connection?.entityId);
                let connectedEntityNode = ctx.scene.getTransformNodeByUniqueId(connectedEntity?.uniqueId ?? 0);
                if (connectedEntityNode) {
                    var connectedEntityConnection = connectedEntity.connections.find(c => { return c.entityId === (entity?.id ?? 0)});
                    var connectedEntityConnector = connectedEntity.connectors.find(c => { return c.id === (connectedEntityConnection?.id ?? 0)});                
                    var connectedEntityConnectorNode = connectedEntityNode.getChildMeshes(true, function (childNode) { return childNode.name === (connectedEntityConnector?.id ?? 0)})[0];

                    if (connectedEntityConnectorNode) {
                       moveEntity(ctx, connectedEntity, entityNode, entityConnectorNode, connectedEntityNode, connectedEntityConnectorNode);
                       moveConnectedEntities(ctx, connectedEntity, traversedIds);
                    }
                }
            }
        });
    }
}

export function moveAllEntities(ctx: AppContext, module: Module) {

    let traversedIds = [] as Array<string>;

    module.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY").forEach(function (entity) {
        if (!traversedIds.includes(entity.id)) {

            traversedIds.push(entity.id);

            let entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);

            entity.connections.forEach(function (connection) {

                let entityConnectorNode = entityNode.getChildMeshes(true, function (c) { return c.name === connection.id })[0];

                var connectedEntity = ctx.moduleHelper.getEntityByID(module, connection.entityId);
                if (connectedEntity) {
                    let connectedEntityNode = ctx.scene.getTransformNodeByUniqueId(connectedEntity.uniqueId);
                    if (connectedEntityNode) {
                        var connectedEntityConnection = connectedEntity.connections.find(c => { return c.entityId === entity.id });
                        if (connectedEntityConnection) {
                            var connectedEntityConnector = connectedEntity.connectors.find(c => { return c.id === connectedEntityConnection.id });
                            if (connectedEntityConnector) {
                                var connectedEntityConnectorNode = connectedEntityNode.getChildMeshes(true, function (childNode) { return childNode.name === connectedEntityConnector.id })[0];

                                moveEntity(ctx, connectedEntity, entityNode, entityConnectorNode, connectedEntityNode, connectedEntityConnectorNode);
                                moveConnectedEntities(ctx, connectedEntity, traversedIds);
                            }
                        }
                    }
                }
            });               
        }
    });

}

export function connectEntities(ctx: AppContext, parentNode: BABYLON.TransformNode, parentConnector: BABYLON.TransformNode, childNode: BABYLON.TransformNode, childConnector: BABYLON.TransformNode) {

    var parentEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, parentNode.id);
    var childEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, childNode.id);

    if (childEntity) {
        var childConnection = childEntity.connections.find(function (c) { return c.id === childConnector.name });
        if (childConnection) {
            var index = childEntity.connections.indexOf(childConnection);
            childEntity.connections.splice(index, 1);
        }

        if (parentEntity) {
            childEntity.connections.push({ id: childConnector.name, entityId: parentEntity.id });
        }
    }

    if (parentEntity) {
        var parentConnection = parentEntity.connections.find(function (c) { return c.id === parentConnector.name });
        if (parentConnection) {
            var index = parentEntity.connections.indexOf(parentConnection);
            parentEntity.connections.splice(index, 1);
        }

        if (childEntity) {
            parentEntity.connections.push({ id: parentConnector.name, entityId: childEntity.id });
        }
    }
}

export function connectNeighbourEntities(ctx: AppContext, entity: Entity) {

    //Get entity node
    var entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
    if (entityNode) {

        entityNode.computeWorldMatrix(true);

        let filteredEntities = ctx.currentModule.entities.filter(e => { return productLibrary.getProductCategory(e.category) === "UPHOLSTRY" && e.id !== entity.id });       
        
        //Iterate entity connectors
        entity.connectors.forEach(function (connector: Connector) {              

            //Determine if connector already has connection             
            var entityConnector = entityNode.getChildMeshes(false, function (childNode) { return childNode.name === connector.id })[0];
            entityConnector.computeWorldMatrix(true);             

            filteredEntities.forEach(function (availableEntity) {                    

                var availableEntityNode = ctx.scene.getTransformNodeByUniqueId(availableEntity.uniqueId);
                if (availableEntityNode) {
                    availableEntityNode.computeWorldMatrix(true);                     

                    var matchingConnectors = getMatchingConnectors(ctx, connector, entity, availableEntity);
                    matchingConnectors.forEach(connectorId => {
                        var matchingConnector = availableEntityNode.getChildMeshes(true, function (childNode) { return childNode.name === connectorId })[0];

                        if (matchingConnector) {
                            matchingConnector.computeWorldMatrix(true);

                            let distance = ConversionHelper.scale(1, ctx.scaleFactor) as number; 

                            if (BABYLON.Vector3.Distance(entityConnector.getAbsolutePosition(), matchingConnector.getAbsolutePosition()) <= distance) {                                   

                                var entityConnection = entity.connections.find(function (connection) { return connection.id === connector.id });
                                if (entityConnection) {
                                    var connectionIndex = entity.connections.indexOf(entityConnection);
                                    entity.connections.splice(connectionIndex, 1);
                                }

                                connectEntities(ctx, entityNode, entityConnector, availableEntityNode, matchingConnector);
                            }
                        }
                    });                       
                }
            });     
        });                         
    }
}

export function insertEntity(ctx: AppContext, entity: Entity, connectorInfos: ConnectorInfo[], placement?: string) {

    var entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);

    var parentConnection = connectorInfos[0];
    var parentConnectorEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, parentConnection.entityNode.id);
    var parentConnector = parentConnectorEntity.connectors.find(c => { return c.id === parentConnection.connectorNode.name });
    var matchingConnectors = getMatchingConnectors(ctx, parentConnector, entity, parentConnectorEntity);
    var parentConnectorNodes = ctx.currentEntityNode.getChildMeshes(true, function (m) { return matchingConnectors.includes(m.name) });

    var parentPlacementConnectorNode = parentConnectorNodes[0];
    if (placement) {           
        parentPlacementConnectorNode = parentConnectorNodes.find(x => x.name.includes(placement));// currentEntityNode.getChildMeshes(true, function (m) { return matchingConnectors.includes(placement ? m.name.split("_")[0] +"_"+ placement : m.name) })[0];
    }

    var childConnection = connectorInfos[1];
    var childConnectorEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, childConnection.entityNode.id);
    var childConnector = childConnectorEntity.connectors.find(c => { return c.id === childConnection.connectorNode.name });
    var matchingConnectors = getMatchingConnectors(ctx, childConnector, entity, childConnectorEntity);
    var childConnectorNodes = ctx.currentEntityNode.getChildMeshes(true, function (m) { return matchingConnectors.includes(m.name) });

     var childPlacementConnectorNode = childConnectorNodes[0];
    if (placement) {           
        childPlacementConnectorNode = childConnectorNodes.find(x => x.name.includes(placement));// currentEntityNode.getChildMeshes(true, function (m) { return matchingConnectors.includes(placement ? m.name.split("_")[0] +"_"+ placement : m.name) })[0];
    }

    connectEntities(ctx, entityNode, parentPlacementConnectorNode, parentConnection.entityNode, parentConnection.connectorNode);
    connectEntities(ctx, childConnection.entityNode, childConnection.connectorNode, entityNode, childPlacementConnectorNode);

    //Transform mesh to parent position
    setTargetRotation(ctx, entity, childConnection.entityNode, childConnection.connectorNode, entityNode, childPlacementConnectorNode);

    //Moved connected entities       
    moveConnectedEntities(ctx, entity, []);
}

//Append entity to current connector
export function appendEntity(ctx: AppContext, entity: Entity, connectorInfo: ConnectorInfo, placement: string) {
  
    if (ctx.currentEntityNode) {
        var parentNode = connectorInfo.entityNode;
        var parentConnector = connectorInfo.connectorNode;
        var childConnector = connectorInfo.sourceConnectorNode;
    
        var entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);// moduleHelper.getEntityByID(currentModule, connectorInfo.entityNode.id);
        entityNode.computeWorldMatrix(true);

        //Determine matching connector
        if (!connectorInfo.sourceConnectorNode) {
            var connectorEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, connectorInfo.entityNode.id);
            var connector = connectorEntity.connectors.find(c => { return c.id === connectorInfo.connectorNode.name });
            var matchingConnectors = getMatchingConnectors(ctx, connector, entity, connectorEntity);  
            
            var childConnectors = entityNode.getChildMeshes(true, function (m) { return matchingConnectors.includes(m.name) });

            childConnector = childConnectors[0];           
            if (placement) {
                childConnector = childConnectors.find(x => x.name.includes(placement));// currentEntityNode.getChildMeshes(true, function (m) { return matchingConnectors.includes(placement ? m.name.split("_")[0] +"_"+ placement : m.name) })[0];
            }
        }

        childConnector.computeWorldMatrix(true)
        
        //Dragged over single unconnected connector
        moveEntity(ctx, entity, parentNode, parentConnector, entityNode, childConnector);

        //Connect neighbouring entities
        connectNeighbourEntities(ctx, entity); 
    }    
}

export function detectCircular(ctx: AppContext, entityId: string) {

    let traversed = [] as Array<string>;

    //Detect
    let detect = function (entityId: string, previousId: string) {

        if (entityId) {

            var entity = ctx.moduleHelper.getEntityByID(ctx.currentModule, entityId);
            if (entity) {

                if (traversed.indexOf(entityId) !== -1) {
                    return true;
                }

                traversed.push(entityId);

                let filteredConnections = entity.connections.filter(function (e) { return e.entityId != previousId });

                for (var i = 0; i < filteredConnections.length; i++) {

                    var connectedEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, filteredConnections[i].entityId);
                    if (connectedEntity) {
                        if (detect(connectedEntity.id, entityId)) {
                            return true;
                        }
                    }
                }
            }
            return false;
        }
    }
    return detect(entityId, null);
}

export function deleteEntity(ctx: AppContext, entity: Entity) {

    let connectors = [];

    let product = productLibrary.getProductByProductNo(entity.productNo);
    let entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
    if (entityNode) {

        //Legs
        const legs = entityNode.getChildTransformNodes(false, x => x.metadata && x.metadata.meshtype === "leg");
        for (let leg of legs) {
            const legEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, leg.id);
            let index = ctx.currentModule.entities.indexOf(legEntity);
            ctx.currentModule.entities.splice(index, 1);
            leg.dispose(false);
        }

        //Are entities connected in a circular loop?
        let isCircular = detectCircular(ctx, entity.id);

        //Get connected connectorinfos
        connectors = getConnectedConnectorInfo(ctx, entity);
        if (connectors.length === 2 && !isCircular) {
            connectEntities(ctx, connectors[0].mesh, connectors[0].connector, connectors[1].mesh, connectors[1].connector);

            if (![148881].includes(product.series)) {
                var connectedEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, connectors[0].mesh.id);                
                moveConnectedEntities(ctx, connectedEntity, []);
            }
        }

        //Remove entity connections
        removeEntityConnections(ctx, entity);

        //Dispose child entities
        var disposableEntities = ctx.currentModule.entities.filter(function (e) { return e.parentId === entity.id});
        disposableEntities.forEach(function (c) {
            deleteEntity(ctx, c);
        });           

        entityNode.dispose(false, true);
        
        ctx.currentEntityNode = null;
        ctx.currentConnector = null;

        //Remove entity from module
        let index = ctx.currentModule.entities.indexOf(entity);
        ctx.currentModule.entities.splice(index, 1);

    }
}

export function cleanupResources(ctx: AppContext, mesh: BABYLON.TransformNode | BABYLON.AbstractMesh) {

    if (mesh instanceof BABYLON.AbstractMesh && !mesh.isAnInstance) {
        if (mesh.material) {
            mesh.material.dispose(true, false);
            mesh.material = null;
        }
    }

    mesh.getChildTransformNodes(false).forEach(function (t) {
        cleanupResources(ctx, t);
    });

    mesh.getChildMeshes(false).forEach(function (m) {
        cleanupResources(ctx, m);
    });

}
